import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CaretLeft, CaretRight, UploadSimple, CalendarBlank } from '@phosphor-icons/react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { ShiftTasksDialog } from '../components/ShiftTasksDialog';
import { ShiftEditDialog } from '../components/ShiftEditDialog';
import { VacationDialog } from '../components/VacationDialog';

const PEOPLE = [
  { name: 'Ladys', bg: 'bg-yellow-300', text: 'text-yellow-950' },
  { name: 'Vee', bg: 'bg-red-500', text: 'text-white' },
  { name: 'Norbi', bg: 'bg-blue-500', text: 'text-white' },
  { name: 'Bösch', bg: 'bg-green-500', text: 'text-white' },
];
const CLOSED = { name: 'Geschlossen', bg: 'bg-orange-400', text: 'text-white' };
const NO_HOURS = ['Norbi', 'Bösch'];
const STYLE = Object.fromEntries([...PEOPLE, CLOSED].map(p => [p.name, p]));
const WD = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const WD_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const fmtH = (m) => (Number.isInteger(m / 60) ? String(m / 60) : (m / 60).toFixed(1));

export default function WorkPlan() {
  const { API, user, language } = useAuth();
  const L = (en, de) => (language === 'de' ? de : en);
  const isAdmin = user?.role === 'admin';
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [plan, setPlan] = useState(null);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState(null);
  const [vacDay, setVacDay] = useState(null);
  const fileRef = useRef(null);
  const month = ym(cursor);

  const load = useCallback(async () => {
    try { const { data } = await API.get(`/work-plans/${month}`); setPlan(data); }
    catch (e) { toast.error(e.response?.data?.detail || L('Could not load', 'Laden fehlgeschlagen')); }
  }, [API, month]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => {
    const n = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    return Array.from({ length: n }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), i + 1));
  }, [cursor]);

  const shifts = plan?.shifts || [];
  const vacations = plan?.vacations || [];
  const byDate = useMemo(() => {
    const m = {};
    shifts.forEach(s => { (m[s.date] = m[s.date] || []).push(s); });
    return m;
  }, [shifts]);
  const totals = useMemo(() => {
    const t = {};
    shifts.filter(s => s.person !== 'Geschlossen').forEach(s => { t[s.person] = (t[s.person] || 0) + toMin(s.end) - toMin(s.start); });
    return t;
  }, [shifts]);
  const firstHour = Math.min(8, ...shifts.filter(s => s.person !== 'Geschlossen').map(s => Math.floor(toMin(s.start) / 60)));
  const hours = Array.from({ length: 24 - firstHour }, (_, i) => firstHour + i);

  const onShiftChanged = (updated) => {
    setPlan(p => ({ ...p, shifts: p.shifts.map(s => (s.date === updated.date && s.person === updated.person && s.start === updated.start ? updated : s)) }));
    setActive(updated);
  };

  const upload = async (file) => {
    try {
      const parsed = JSON.parse(await file.text());
      const body = Array.isArray(parsed) ? { shifts: parsed } : parsed;
      await API.put(`/work-plans/${month}`, body);
      toast.success(L('Work plan uploaded', 'Arbeitsplan hochgeladen'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Invalid file (JSON expected)', 'Ungültige Datei (JSON erwartet)'));
    } finally { if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div className="space-y-6" data-testid="work-plan-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">{L('Work plan', 'Arbeitsplan')}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {plan?.updated_at
              ? L(`Last updated ${new Date(plan.updated_at).toLocaleString('de-CH')}`, `Zuletzt aktualisiert ${new Date(plan.updated_at).toLocaleString('de-CH')}`)
              : L('No plan uploaded for this month yet', 'Für diesen Monat ist noch kein Plan hochgeladen')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="work-plan-prev">
            <CaretLeft size={16} />
          </Button>
          <div className="min-w-[150px] text-center font-bold text-slate-800" data-testid="work-plan-month">
            {cursor.toLocaleDateString('de-CH', { month: 'long', year: 'numeric' })}
          </div>
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="work-plan-next">
            <CaretRight size={16} />
          </Button>
          {isAdmin && (
            <>
              <input ref={fileRef} type="file" accept=".json,application/json" className="hidden"
                onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="work-plan-file-input" />
              <Button onClick={() => fileRef.current?.click()} className="bg-slate-900 text-white hover:bg-slate-700" data-testid="work-plan-upload-btn">
                <UploadSimple size={16} className="mr-2" /> {L('Upload plan', 'Plan hochladen')}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="work-plan-legend">
        {PEOPLE.map(p => (
          <div key={p.name} className="flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-1 pr-3 py-1" data-testid={`work-plan-legend-${p.name}`}>
            <span className={`w-6 h-6 rounded-full ${p.bg}`} />
            <span className="text-sm font-bold text-slate-800">{p.name}</span>
            {!NO_HOURS.includes(p.name) && <span className="text-xs font-semibold text-slate-500">{fmtH(totals[p.name] || 0)} Std.</span>}
          </div>
        ))}
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-1 pr-3 py-1">
          <span className={`w-6 h-6 rounded-full ${CLOSED.bg}`} />
          <span className="text-sm font-bold text-slate-800">{L('Closed', 'Geschlossen')}</span>
        </div>
      </div>

      {shifts.length === 0 && vacations.length === 0 && !isAdmin ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center text-slate-500" data-testid="work-plan-empty">
          <CalendarBlank size={36} className="mx-auto mb-2 text-slate-300" />
          {L('No shifts for this month.', 'Keine Schichten für diesen Monat.')}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="work-plan-grid">
          <table className="border-collapse text-xs">
            <thead>
              <tr>
                <th className="sticky left-0 z-20 bg-slate-50 border-b border-r border-slate-200 px-3 py-2 text-left font-bold text-slate-600 min-w-[96px]">
                  {L('Time', 'Zeit')}
                </th>
                {days.map(d => {
                  const iso = `${month}-${String(d.getDate()).padStart(2, '0')}`;
                  const weekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <th key={iso} className={`border-b border-r border-slate-200 px-1 py-2 min-w-[64px] font-semibold ${weekend ? 'bg-slate-100' : 'bg-slate-50'}`}>
                      <div className="text-slate-800">{d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' })}</div>
                      <div className="text-[10px] text-slate-500 font-medium">{(language === 'de' ? WD : WD_EN)[d.getDay()]}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <tr data-testid="wp-vacation-row">
                <td className="sticky left-0 z-10 bg-slate-50 border-b-2 border-r border-slate-200 px-3 py-1 font-bold text-slate-700">
                  {L('Holidays', 'Ferien')}
                </td>
                {days.map(d => {
                  const iso = `${month}-${String(d.getDate()).padStart(2, '0')}`;
                  const off = vacations.filter(v => v.start_date <= iso && v.end_date >= iso);
                  return (
                    <td key={iso} onClick={() => isAdmin && setVacDay(iso)} data-testid={`wp-vac-${iso}`}
                      className={`border-b-2 border-r border-slate-200 px-0.5 py-1 align-top min-h-[28px] ${isAdmin ? 'cursor-pointer hover:bg-slate-50' : ''}`}>
                      <div className="flex flex-col gap-0.5">
                        {off.map(v => (
                          <span key={v.id} title={`${v.person} ${v.note || ''}`}
                            className={`${STYLE[v.person]?.bg} ${STYLE[v.person]?.text} rounded text-[9px] font-bold text-center leading-4 truncate`}>
                            {v.person}
                          </span>
                        ))}
                      </div>
                    </td>
                  );
                })}
              </tr>
              {hours.map(h => (
                <tr key={h}>
                  <td className="sticky left-0 z-10 bg-white border-b border-r border-slate-200 px-3 py-0 h-7 font-semibold text-slate-600 whitespace-nowrap">
                    {String(h).padStart(2, '0')}:00 – {String(h + 1).padStart(2, '0')}:00
                  </td>
                  {days.map(d => {
                    const iso = `${month}-${String(d.getDate()).padStart(2, '0')}`;
                    const list = byDate[iso] || [];
                    const closed = list.find(s => s.person === 'Geschlossen' && toMin(s.start) <= h * 60 && toMin(s.end) > h * 60);
                    const on = list.filter(s => s.person !== 'Geschlossen' && toMin(s.start) < (h + 1) * 60 && toMin(s.end) > h * 60);
                    return (
                      <td key={iso} className="border-b border-r border-slate-100 p-0 h-7" data-testid={`wp-cell-${iso}-${h}`}>
                        {closed ? (
                          <div onClick={() => isAdmin && setDraft({ ...closed, orig: { date: closed.date, person: closed.person, start: closed.start } })}
                            className={`h-full w-full ${CLOSED.bg} ${CLOSED.text} flex items-center justify-center text-[9px] font-bold ${isAdmin ? 'cursor-pointer' : ''}`}>
                            {h === hours[0] || toMin(closed.start) === h * 60 ? L('Closed', 'Geschl.') : ''}
                          </div>
                        ) : (
                          <div className={`flex h-full w-full ${isAdmin && on.length === 0 ? 'cursor-pointer hover:bg-slate-100 transition-colors' : ''}`}
                            onClick={() => isAdmin && on.length === 0 && setDraft({ date: iso, start: `${String(h).padStart(2, '0')}:00`, end: `${String(Math.min(h + 8, 24)).padStart(2, '0')}:00` })}>
                            {on.map(s => {
                              const st = STYLE[s.person];
                              const isStart = Math.floor(toMin(s.start) / 60) === h;
                              return (
                                <div key={`${s.person}-${s.start}`} onClick={() => setActive(s)}
                                  className={`flex-1 ${st.bg} ${st.text} flex items-center justify-center font-bold cursor-pointer hover:brightness-110 transition-[filter]`}
                                  title={`${s.person} ${s.start}–${s.end}`} data-testid={isStart ? `wp-shift-${s.date}-${s.person}-${s.start}` : undefined}>
                                  {isStart ? fmtH(toMin(s.end) - toMin(s.start)) : ''}
                                  {isStart && s.tasks?.length > 0 && (
                                    <span className="ml-1 text-[9px] font-semibold opacity-90">
                                      ✓{s.tasks.filter(t => t.done).length}/{s.tasks.length}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ShiftTasksDialog month={month} shift={active} onClose={() => setActive(null)} onChanged={onShiftChanged}
        onEdit={(s) => { setActive(null); setDraft({ ...s, orig: { date: s.date, person: s.person, start: s.start } }); }} />
      <ShiftEditDialog month={month} draft={draft} onClose={() => setDraft(null)} onSaved={() => { setDraft(null); load(); }} />
      <VacationDialog month={month} day={vacDay} vacations={vacations} onClose={() => setVacDay(null)} onSaved={() => { setVacDay(null); load(); }} />
    </div>
  );
}
